import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { collection, onSnapshot, doc, setDoc, deleteDoc, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import ImageUploader from '../../components/admin/ImageUploader';

interface Project {
  id: string;
  title: string;
  location: string;
  status: 'Tamamlandı' | 'Devam Ediyor' | 'Planlanıyor';
  cover_image: string;
  description: string;
}

export default function ProjectsManager() {
  const [projects, setProjects] = useState<Project[]>([]);
  const [loading, setLoading] = useState(true);
  
  // Modal State
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);

  // Form State
  const [formTitle, setFormTitle] = useState('');
  const [formLocation, setFormLocation] = useState('');
  const [formStatus, setFormStatus] = useState<'Tamamlandı' | 'Devam Ediyor' | 'Planlanıyor'>('Devam Ediyor');
  const [formCoverImage, setFormCoverImage] = useState('');
  const [formDesc, setFormDesc] = useState('');
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    const colRef = collection(db, 'projects');
    const unsubscribe = onSnapshot(colRef, async (snap) => {
      // Auto-Migration if empty
      if (snap.empty) {
        console.log("Projects collection empty, auto-seeding...");
        const initialProjects = [
          { title: "Kare Modern Rezidans", location: "Ataşehir, İstanbul", status: "Tamamlandı" as const, cover_image: "/premium-project-block.png", description: "Lüks mimarisi ve geniş sosyal alanlarıyla Ataşehir'in yeni yıldızı.", createdAt: new Date(), updatedAt: new Date() },
          { title: "İkiz Park Evleri", location: "Ümraniye, İstanbul", status: "Devam Ediyor" as const, cover_image: "/premium-project-v3.png", description: "Şehrin merkezinde yemyeşil bir yaşam alanı projesi.", createdAt: new Date(), updatedAt: new Date() },
          { title: "Atlas Prestij Butik", location: "Kadıköy, İstanbul", status: "Planlanıyor" as const, cover_image: "/yapsat-realistic-1.png", description: "Boutique konsept ile kentsel dönüşümün elit yüzü.", createdAt: new Date(), updatedAt: new Date() },
        ];
        // Note: we just fire these off, the next snapshot will catch them.
        for (const p of initialProjects) {
          await addDoc(colRef, p);
        }
        return;
      }

      const items: Project[] = [];
      snap.forEach(doc => {
        items.push({ id: doc.id, ...doc.data() } as Project);
      });
      // Sort by creation date just for safety, or leave as is
      setProjects(items);
      setLoading(false);
    });
    return () => unsubscribe();
  }, []);

  const openModal = (project?: Project) => {
    if (project) {
      setEditingProject(project);
      setFormTitle(project.title || '');
      setFormLocation(project.location || '');
      setFormStatus(project.status || 'Devam Ediyor');
      setFormCoverImage(project.cover_image || '');
      setFormDesc(project.description || '');
    } else {
      setEditingProject(null);
      setFormTitle('');
      setFormLocation('');
      setFormStatus('Devam Ediyor');
      setFormCoverImage('');
      setFormDesc('');
    }
    setIsModalOpen(true);
  };

  const closeModal = () => {
    setIsModalOpen(false);
    setEditingProject(null);
  };

  const handleSave = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    try {
      const projectData = {
        title: formTitle,
        location: formLocation,
        status: formStatus,
        cover_image: formCoverImage,
        description: formDesc,
        updatedAt: new Date()
      };

      if (editingProject) {
        // Update
        const docRef = doc(db, 'projects', editingProject.id);
        await setDoc(docRef, projectData, { merge: true });
      } else {
        // Create
        const colRef = collection(db, 'projects');
        await addDoc(colRef, { ...projectData, createdAt: new Date() });
      }
      closeModal();
    } catch (err) {
      console.error(err);
      alert('Proje kaydedilemedi.');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string, title: string) => {
    if (!window.confirm(`'${title}' adlı projeyi kalıcı olarak silmek istediğinize emin misiniz?`)) return;
    try {
      await deleteDoc(doc(db, 'projects', id));
    } catch (err) {
      console.error(err);
      alert('Proje silinemedi.');
    }
  };

  if (loading) return <div className="text-zinc-500">Projeler yükleniyor...</div>;

  return (
    <div className="space-y-6 pb-24 relative">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 mb-6">
        <div>
          <h2 className="text-2xl font-black text-white" style={{ fontFamily: 'Raleway, sans-serif' }}>Projeler & Katalog</h2>
          <p className="text-zinc-400 text-sm mt-1">İmza projelerinizi ve katalogda sergilenen yapıları buradan yönetin.</p>
        </div>
        <button onClick={() => openModal()} className="bg-red-600 hover:bg-red-700 text-white px-5 py-2.5 rounded-xl font-bold text-sm shadow-lg shadow-red-600/20 transition-all flex items-center gap-2">
          <i className="ri-add-line text-lg"></i> Yeni Proje Ekle
        </button>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-left border-collapse">
            <thead>
              <tr className="bg-zinc-950/50 border-b border-zinc-800 text-zinc-400 text-xs uppercase tracking-wider">
                <th className="p-4 font-bold">Görsel</th>
                <th className="p-4 font-bold">Proje Adı</th>
                <th className="p-4 font-bold hidden md:table-cell">Konum</th>
                <th className="p-4 font-bold">Durum</th>
                <th className="p-4 font-bold text-right">İşlemler</th>
              </tr>
            </thead>
            <tbody>
              {projects.length === 0 ? (
                <tr>
                  <td colSpan={5} className="p-8 text-center text-zinc-500">Henüz hiç proje eklenmemiş.</td>
                </tr>
              ) : (
                projects.map((project) => (
                  <tr key={project.id} className="border-b border-zinc-800/50 hover:bg-zinc-800/20 transition-colors">
                    <td className="p-4 cursor-pointer" onClick={() => openModal(project)}>
                      <div className="w-16 h-12 bg-zinc-800 rounded-lg overflow-hidden relative">
                        {project.cover_image ? (
                          <img src={project.cover_image} className="w-full h-full object-cover" alt={project.title} />
                        ) : (
                          <i className="ri-image-line absolute inset-0 flex items-center justify-center text-zinc-600"></i>
                        )}
                      </div>
                    </td>
                    <td className="p-4 font-bold text-white cursor-pointer" onClick={() => openModal(project)}>{project.title}</td>
                    <td className="p-4 text-zinc-400 text-sm hidden md:table-cell">{project.location}</td>
                    <td className="p-4">
                      <span className={`px-3 py-1 text-xs font-bold rounded-full ${
                        project.status === 'Tamamlandı' ? 'bg-green-500/10 text-green-500 border border-green-500/20' :
                        project.status === 'Devam Ediyor' ? 'bg-amber-500/10 text-amber-500 border border-amber-500/20' :
                        'bg-blue-500/10 text-blue-500 border border-blue-500/20'
                      }`}>
                        {project.status}
                      </span>
                    </td>
                    <td className="p-4 text-right space-x-2">
                       <button onClick={() => openModal(project)} className="text-zinc-400 hover:text-white transition-colors p-2 bg-zinc-800 rounded-lg hover:bg-zinc-700" title="Düzenle">
                         <i className="ri-edit-2-line"></i>
                       </button>
                       <button onClick={() => handleDelete(project.id, project.title)} className="text-red-500 hover:text-red-400 transition-colors p-2 bg-red-500/10 rounded-lg hover:bg-red-500/20" title="Sil">
                         <i className="ri-delete-bin-line"></i>
                       </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Editor Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeModal}></div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="bg-zinc-900 border border-zinc-800 rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto relative z-10 shadow-2xl">
            <div className="sticky top-0 bg-zinc-900/90 backdrop-blur-md border-b border-zinc-800 p-5 flex justify-between items-center z-20">
              <h3 className="text-lg font-bold text-white uppercase tracking-wider flex items-center gap-2">
                <i className="ri-building-line text-red-500"></i>
                {editingProject ? 'Projeyi Düzenle' : 'Yeni Proje Oluştur'}
              </h3>
              <button onClick={closeModal} className="text-zinc-400 hover:text-white p-1">
                <i className="ri-close-line text-2xl"></i>
              </button>
            </div>
            
            <form onSubmit={handleSave} className="p-6 space-y-6">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div>
                  <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Proje Adı</label>
                  <input type="text" required value={formTitle} onChange={e => setFormTitle(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" placeholder="Örn: Atlas Residence" />
                </div>
                <div>
                  <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Konum / Şehir</label>
                  <input type="text" required value={formLocation} onChange={e => setFormLocation(e.target.value)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" placeholder="Örn: Kadıköy, İstanbul" />
                </div>
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Proje Durumu</label>
                <select value={formStatus} onChange={e => setFormStatus(e.target.value as any)} className="w-full bg-zinc-950 border border-zinc-800 text-white rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none appearance-none">
                  <option value="Devam Ediyor">Devam Ediyor (Şantiye)</option>
                  <option value="Tamamlandı">Tamamlandı (Teslim Edildi)</option>
                  <option value="Planlanıyor">Planlanıyor (Yakında)</option>
                </select>
              </div>

              <div>
                <label className="block text-zinc-400 text-xs font-bold mb-1 uppercase tracking-wider">Proje Detayları & Açıklama</label>
                <textarea required value={formDesc} onChange={e => setFormDesc(e.target.value)} rows={4} className="w-full bg-zinc-950 border border-zinc-800 text-zinc-300 rounded-xl py-3 px-4 focus:border-red-500 focus:outline-none" placeholder="Projenin mimari yapısı, m2 bilgileri, sosyal donatıları..."></textarea>
              </div>

              <div className="pt-2 border-t border-zinc-800/50">
                 <ImageUploader 
                   currentImageUrl={formCoverImage} 
                   onImageUploaded={(url) => setFormCoverImage(url)} 
                   onImageDeleted={() => setFormCoverImage('')} 
                   folderPath={`projects/${formTitle.replace(/\s+/g, '_').toLowerCase()}`}
                   label="Proje Kapak Görseli (Katalogda Görünecek)"
                 />
              </div>

              <div className="pt-6 flex justify-end gap-3">
                <button type="button" onClick={closeModal} className="px-6 py-3 rounded-xl font-bold text-sm text-zinc-400 hover:text-white transition-colors">İptal</button>
                <button type="submit" disabled={saving} className="bg-red-600 hover:bg-red-700 text-white px-8 py-3 rounded-xl font-bold text-sm disabled:opacity-50 flex items-center gap-2 shadow-lg shadow-red-600/20">
                  {saving ? <i className="ri-loader-4-line animate-spin"></i> : <i className="ri-save-fill"></i>}
                  {saving ? 'Kaydediliyor...' : 'Projeyi Kaydet'}
                </button>
              </div>
            </form>
          </motion.div>
        </div>
      )}

    </div>
  );
}
